import { forwardRef, useContext, useMemo } from "react"
import { useTheme } from "@mui/material"
import { visuallyHidden } from "@mui/utils"
import { useSnapshot } from "valtio"
import AnswerBubble from "../../../ui/bubbles/AnswerBubble"
import CheckboxComponent from "../../../ui/inputs/CheckboxComponent"
import { STAAR_MULTIPLE_CHOICE_ANSWER } from "../../../constants/staarTools"
import { percentageFormatter } from "../../../helpers/formatters"
import { getAnswerChoiceAriaLabel } from "../../../helpers/mathAccessibility"
import { MathSpeakableContext } from "../../../context/MathSpeakableContext"
import { QuestionContextMenuContext } from "../../../context/QuestionContextMenuContext"
import { IconColorContext } from "../../../context/IconColorContext"
import {
  BubbleWrapper,
  ContentWrapper,
  PercentageLabel,
  SimpleChoiceWrapper
} from "./styles"
import { CheckCorrect, NeutralSetting, Variant } from "../../../types"
import { ScoringState } from "../../../storage/scoring"

export interface SimpleChoiceProps {
  letterIndex?: string
  itemId: string
  identifier?: string
  fixed?: boolean
  isCorrect?: boolean
  groupMode?: boolean
  feedbackPercentage?: number
  feedbackFormatter?: (percentage: number) => string
  availableMultipleChoices?: boolean
  onSelect?: (id: string) => void
  selectedChoices?: string[]
  children?: JSX.Element
  accessibilityAttr?: { [key: string]: string }
  disabled?: boolean
  setFocusedChoice?: () => void
  arrowKeysNavigation?: (e: React.KeyboardEvent<Element>) => void
  isFocused?: boolean
  onChoiceFocus?: () => void
  blankBubble?: boolean
  checkCorrect?: CheckCorrect
  completed?: boolean
  isNeutralSetting?: NeutralSetting
}

const defaultFeedbackFormatter = (percentage: number) =>
  percentageFormatter.format(percentage)

const CHOICE_MATH_CONTEXT = { onSpeakableText: () => {} }

export const SimpleChoiceComponent = forwardRef<
  HTMLDivElement,
  SimpleChoiceProps
>((props, ref) => {
  const {
    letterIndex,
    identifier,
    availableMultipleChoices,
    isCorrect,
    groupMode = false,
    feedbackPercentage,
    feedbackFormatter = defaultFeedbackFormatter,
    onSelect,
    setFocusedChoice,
    arrowKeysNavigation,
    isFocused,
    onChoiceFocus,
    selectedChoices = [],
    accessibilityAttr,
    disabled,
    blankBubble = false,
    checkCorrect,
    completed,
    isNeutralSetting
  } = props

  const theme = useTheme()
  const { contentPreviewMode } = useSnapshot(ScoringState)
  const { stikethroughAnswers = [] } = useContext(QuestionContextMenuContext)
  const isStrikethrough = stikethroughAnswers.includes(identifier)

  const selected = useMemo(
    () =>
      selectedChoices.includes(identifier) ||
      (completed && !isNeutralSetting && checkCorrect?.(identifier)),
    [selectedChoices, identifier, completed, checkCorrect, isNeutralSetting]
  )

  const badgeClass: { variant: Variant | null } = useMemo(() => {
    if (selected && isNeutralSetting) {
      return { variant: Variant.neutral }
    }
    if (selected && checkCorrect) {
      return checkCorrect?.(identifier)
        ? { variant: Variant.correct }
        : { variant: Variant.invalid }
    }
    return { variant: null }
  }, [selected, isNeutralSetting, checkCorrect, identifier])

  const isReviewMode = isCorrect !== undefined || groupMode
  const contentDescriptionId = `${identifier}-content`
  const choiceLabelId = `${identifier}-choice-aria-label`

  const choiceAriaLabel = useMemo(() => {
    return letterIndex
      ? !isReviewMode || groupMode
        ? getAnswerChoiceAriaLabel(letterIndex, "none")
        : getAnswerChoiceAriaLabel(
            letterIndex,
            isCorrect ? "correct" : "incorrect"
          )
      : undefined
  }, [letterIndex, isReviewMode, groupMode, isCorrect])

  const onClickHandler = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation()
    if (completed) return
    onSelect?.(identifier)
    setFocusedChoice && setFocusedChoice()
  }

  const onKeyboardSubmitHandler = (e: React.KeyboardEvent) => {
    if ((e.code === "Space" || e.code === "Enter") && !completed) {
      e.preventDefault()
      onSelect?.(identifier)
    }

    !!arrowKeysNavigation && arrowKeysNavigation(e)
  }

  // a11y: role + visual follow availableMultipleChoices — multi unless the item is capped at
  // exactly one choice (maxChoices === 1), which is the only single-select case per the hook.
  const isCheckbox = availableMultipleChoices && !letterIndex

  const choiceAccessibleNameIds = choiceAriaLabel
    ? `${choiceLabelId} ${contentDescriptionId}`
    : contentDescriptionId

  return (
    <MathSpeakableContext.Provider value={CHOICE_MATH_CONTEXT}>
      <SimpleChoiceWrapper
        ref={ref}
        isFocused={isFocused}
        isReviewMode={isReviewMode}
        disabled={!selected && disabled}
        className={STAAR_MULTIPLE_CHOICE_ANSWER}
        id={identifier}
        role={availableMultipleChoices ? "checkbox" : "radio"}
        aria-checked={selected}
        // `disabled` goes true for every choice once maxChoices is reached, but a selected
        // answer can still be deselected — so mark them unavailable rather than unreachable.
        aria-disabled={isReviewMode || (disabled && !selected) ? true : undefined}
        aria-labelledby={choiceAccessibleNameIds}
        onFocus={onChoiceFocus}
        // Each choice is its own tab stop (Tab: A -> B -> C -> D); arrows still work too.
        tabIndex={0}
        {...(isReviewMode && !!arrowKeysNavigation
          ? { onKeyDown: arrowKeysNavigation }
          : {})}
        {...(!isReviewMode
          ? {
              onClick: onClickHandler,
              onKeyDown: onKeyboardSubmitHandler
            }
          : {})}
        {...badgeClass}
      >
        {choiceAriaLabel && (
          <span id={choiceLabelId} style={visuallyHidden}>
            {choiceAriaLabel}
          </span>
        )}
        {groupMode &&
          !contentPreviewMode &&
          feedbackPercentage !== undefined && (
          <PercentageLabel correct={isCorrect} as="span">
            {feedbackFormatter(feedbackPercentage)}
          </PercentageLabel>
        )}
        <BubbleWrapper blankBubble={blankBubble}>
          {isCheckbox ? (
            <CheckboxComponent
              presentationOnly
              disabled={!selected && (disabled || isReviewMode)}
              checked={selected}
              correct={isCorrect}
              identifier={identifier}
              {...badgeClass}
            />
          ) : (
            <AnswerBubble
              selected={selected}
              correct={isCorrect}
              symbol={letterIndex}
              identifier={identifier}
              accessibilityAttr={accessibilityAttr}
              tabIndex={-1}
              focusable={false}
              presentationOnly
              {...badgeClass}
            />
          )}
        </BubbleWrapper>
        <IconColorContext.Provider
          value={badgeClass.variant ? theme.palette.blueGrey[100] : undefined}
        >
          <ContentWrapper
            id={contentDescriptionId}
            isStrikethrough={isStrikethrough}
            {...badgeClass}
          >
            {props.children}
          </ContentWrapper>
        </IconColorContext.Provider>
      </SimpleChoiceWrapper>
    </MathSpeakableContext.Provider>
  )
})
